import type { OrgAnnouncement } from '../../../lib/announcements';

/**
 * 저작 목록의 **노출 상태** — 게시·기간 축 하나.
 *
 * `DRAFT`(미게시) · `SCHEDULED`(게시됐지만 시작 전) · `LIVE`(지금 보인다) · `ENDED`(기간 종료 —
 * **게시판에는 남는다**).
 */
export type AnnouncementState = 'DRAFT' | 'SCHEDULED' | 'LIVE' | 'ENDED';

export interface AnnouncementVisibility {
  state: AnnouncementState;
  /** 작성자가 내려 둔 상태 — **게시·기간과 독립이라 state에 섞지 않는다**(아래 JSDoc) */
  hidden: boolean;
}

/**
 * 저작 화면의 상태 파생 — **축이 둘이고 한 라벨로 접지 않는다.**
 *
 * `hidden`을 `state`에 섞어 한 단어로 만들면(예: 「숨김」이 「초안」을 덮으면) **작성자가 게시를
 * 눌렀는데 아무것도 안 뜨는** 자리가 생긴다 — 서버 게시판 조건이 `게시됨 ∧ 숨김 아님 ∧ 시작됨`
 * 세 항의 **곱**이라(`publishedCandidateFilters`) 한 항만 고쳐도 안 보인다. 그래서 두 축을 **따로
 * 돌려주고** 화면이 배지를 둘 그린다.
 *
 * ⚠️ `ENDED`는 **「끝났으니 사라진다」가 아니다** — 게시판은 기간 상한을 보지 않으므로(보존 축)
 * 그 공지는 목록에 남고 고정 띠에서만 빠진다(`listPinnedBanners` ↔ `listBoard`의 가름).
 *
 * ⚠️ 경계는 **서버와 같은 방향**으로 둔다: 시작은 `startsAt <= now`에서 보이고(서버
 * `startsAt: { lte: now }`), 종료는 `endsAt > now`까지 보인다(서버 `endsAt: { gt: now }`). 한쪽을
 * 반대로 두면 경계 1초에서 화면과 서버가 **다른 답**을 낸다.
 */
export function announcementVisibility(
  row: Pick<OrgAnnouncement, 'publishedAt' | 'startsAt' | 'endsAt' | 'hidden'>,
  now: Date = new Date(),
): AnnouncementVisibility {
  const hidden = row.hidden;
  if (!row.publishedAt) return { state: 'DRAFT', hidden };

  const ms = now.getTime();
  if (row.endsAt && new Date(row.endsAt).getTime() <= ms) return { state: 'ENDED', hidden };
  if (row.startsAt && new Date(row.startsAt).getTime() > ms) return { state: 'SCHEDULED', hidden };
  return { state: 'LIVE', hidden };
}

/** 화면 문구 — 상태 넷 전부에 대해 exhaustive하다(값이 늘면 tsc가 여기서 깨진다) */
export const ANNOUNCEMENT_STATE_LABELS: Record<AnnouncementState, string> = {
  DRAFT: '초안',
  SCHEDULED: '게시 예정',
  LIVE: '게시 중',
  ENDED: '기간 종료',
};
